import { Building2, AlertTriangle } from 'lucide-react';
import { useLocation } from 'wouter';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface BeneficiaryRoleCardProps {
  beneficiary: {
    id: string;
    trustAccountId: string;
    trustName: string;
    beneficiaryClass: string | null;
    unitPercentage: number | null;
    isBeneficialOwner: boolean;
    isActive: boolean;
  };
}

export default function BeneficiaryRoleCard({ beneficiary }: BeneficiaryRoleCardProps) {
  const [, navigate] = useLocation();

  const isCeased = !beneficiary.isActive;

  return (
    <Card
      className={cn(
        'border-l-4',
        isCeased ? 'border-l-muted opacity-60' : 'border-l-yellow-500'
      )}
    >
      <CardContent className="p-3">
        <div className="flex justify-between items-start">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className={cn(
                'font-medium text-sm',
                isCeased && 'line-through'
              )}>
                {beneficiary.trustName}
              </span>

              {beneficiary.isBeneficialOwner && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="default" className="bg-amber-500 text-white hover:bg-amber-600 text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        UBO
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Ultimate Beneficial Owner</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {beneficiary.beneficiaryClass && (
                <Badge variant="secondary" className="bg-purple-500/10 text-purple-700 dark:text-purple-400 text-xs">
                  {beneficiary.beneficiaryClass.replace('_', ' ')}
                </Badge>
              )}

              {isCeased && (
                <Badge variant="secondary" className="text-xs">Ceased</Badge>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Trust Beneficiary
            </p>

            {beneficiary.unitPercentage !== null && (
              <p className="text-xs text-muted-foreground">
                Entitlement: <span className="font-medium">{beneficiary.unitPercentage.toFixed(2)}%</span>
              </p>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/accounts/${beneficiary.trustAccountId}`)}
          >
            View Trust
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
